import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { SubscribeUserRequest, SubscriptionUserListItem, UserUpdate } from '../../models';

export interface UserFormDialogData {
  user?: SubscriptionUserListItem;
}

@Component({
  selector: 'app-user-form-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatIconModule],
  template: `
    <div class="app-dialog">
      <div class="app-dialog__heading">
        <button mat-icon-button type="button" class="user-dialog-close" aria-label="Close" (click)="dialogRef.close()">
          <mat-icon>close</mat-icon>
        </button>
        <div class="app-dialog__heading-copy">
          <span class="app-dialog__eyebrow">User management</span>
          <div class="app-dialog__title-row">
            <div class="app-dialog__title-copy">
              <h2 mat-dialog-title class="app-dialog__title">{{ data.user ? 'Edit user' : 'Create user' }}</h2>
              <p>{{ data.user ? 'Update the profile details for this account.' : 'Add an account and assign its initial access details.' }}</p>
            </div>
          </div>
        </div>
      </div>
    <form #userForm="ngForm" (ngSubmit)="save()" novalidate>
      <mat-dialog-content class="app-dialog__content">
        <section class="app-dialog__section">
          <div class="app-dialog__section-heading">
            <h3>Account details</h3>
            <span>Required fields are marked with *</span>
          </div>
          <div class="app-dialog__grid">
            <mat-form-field appearance="outline" class="app-dialog__full-width">
              <mat-label>Username</mat-label>
              <input matInput name="userName" #userNameField="ngModel" [(ngModel)]="form.userName" [disabled]="!!data.user" required autocomplete="username" placeholder="name@example.com">
              <mat-error *ngIf="userNameField.invalid && (userNameField.dirty || userNameField.touched)">Enter a username.</mat-error>
            </mat-form-field>
            <!-- <mat-form-field *ngIf="!data.user" appearance="outline">
              <mat-label>User type</mat-label>
              <mat-select name="userTypeId" [(ngModel)]="form.userTypeId" required>
                <mat-option *ngFor="let type of userTypes" [value]="type.id">{{ type.label }}</mat-option>
              </mat-select>
              <mat-hint>Controls the account's initial role.</mat-hint>
            </mat-form-field>
            <mat-form-field *ngIf="!data.user" appearance="outline">
              <mat-label>Product</mat-label>
              <input matInput name="product" [(ngModel)]="form.product" required autocomplete="off" placeholder="DQP">
              <mat-hint>Product code for the subscription.</mat-hint>
            </mat-form-field> -->
            <mat-form-field *ngIf="!data.user" appearance="outline" class="app-dialog__full-width">
              <mat-label>Temporary password</mat-label>
              <input matInput name="password" #passwordField="ngModel" type="password" [(ngModel)]="form.password" required autocomplete="new-password" placeholder="Enter a temporary password">
              <mat-error *ngIf="passwordField.invalid && (passwordField.dirty || passwordField.touched)">Enter a temporary password.</mat-error>
              <mat-hint>The user can change this after signing in.</mat-hint>
            </mat-form-field>
          </div>
        </section>
        <section class="app-dialog__section profile-section">
          <div class="app-dialog__section-heading">
            <h3>Profile</h3>
            <span>Optional</span>
          </div>
          <div class="app-dialog__grid">
            <mat-form-field appearance="outline">
              <mat-label>First name</mat-label>
              <input matInput name="firstName" [(ngModel)]="form.firstName" autocomplete="given-name">
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Last name</mat-label>
              <input matInput name="lastName" [(ngModel)]="form.lastName" autocomplete="family-name">
            </mat-form-field>
          </div>
        </section>
      </mat-dialog-content>
      <mat-dialog-actions class="app-dialog__actions" align="end">
        <button mat-button type="button" (click)="dialogRef.close()">Cancel</button>
        <button mat-raised-button color="primary" type="submit" [disabled]="userForm.invalid || !form.userName.trim() || (!data.user && (!form.password.trim() || !form.product.trim()))">{{ data.user ? 'Save changes' : 'Create user' }}</button>
      </mat-dialog-actions>
    </form>
    </div>
  `,
  styles: [`
    .user-dialog-close { position: absolute !important; top: 12px !important; right: 16px !important; z-index: 1; color: var(--text-secondary); }
    .profile-section .app-dialog__grid { padding-bottom: 16px; }
  `]
})
export class UserFormDialogComponent {
  form: {
    userName: string;
    firstName: string;
    lastName: string;
    password: string;
    userTypeId: number;
    product: string;
  };

  readonly userTypes = [{ id: 2, label: 'Regular user' }];

  constructor(
    public dialogRef: MatDialogRef<UserFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UserFormDialogData
  ) {
    this.form = {
      userName: this.data.user?.userName ?? '',
      firstName: this.data.user?.firstName ?? '',
      lastName: this.data.user?.lastName ?? '',
      password: '',
      userTypeId: 2,
      product: 'DQP'
    };
  }

  save(): void {
    const result: SubscribeUserRequest | UserUpdate = this.data.user
      ? { firstName: this.form.firstName, lastName: this.form.lastName }
      : {
        UserName: this.form.userName,
        UserTypeId: this.form.userTypeId,
        Password: this.form.password,
        Product: this.form.product,
        FirstName: this.form.firstName || undefined,
        LastName: this.form.lastName || undefined
      };
    this.dialogRef.close(result);
  }
}